import { useState } from 'react'
import type { ShipDto } from '@/api/hooks'
import { SHIP_TYPE_ORDER } from '@/lib/fleetGroups'
import { ShipIcon, ModuleIcon } from '@/components/game/GameIcons'
import { ANY_MODULE, SORT_LABEL, type ShipFilter } from './useShipFilter'

/** Formes courtes des coques, déjà écrites au joueur (Codex : « Brick 2,
 *  Frégate 3, Galion 4, Légendaire 5 »). La forme longue « Navire Légendaire »
 *  reste pour les tableaux. */
const HULL_SHORT: Record<string, string> = {
  brick: 'Brick', fregate: 'Frégate', galion: 'Galion', legendary: 'Légendaire',
}

/** Les sept modules, dans l'ordre de l'enum serveur, avec le libellé exact du Codex. */
const MODULE_FILTERS: { type: string; label: string; combat: boolean }[] = [
  { type: 'long_cannons', label: 'Canons longs', combat: true },
  { type: 'boarding_hooks', label: "Grappins d'abordage", combat: true },
  { type: 'streamlined_keel', label: 'Quille profilée', combat: true },
  { type: 'reinforced_hold', label: 'Cale renforcée', combat: false },
  { type: 'racing_rig', label: 'Gréement de course', combat: false },
  { type: 'false_bottom', label: 'Faux-fond', combat: false },
  { type: 'spyglass', label: 'Longue-vue', combat: false },
]

const CHIP_BASE = 'min-h-8 inline-flex items-center gap-1.5 px-2.5 rounded-full border text-[11px] transition-colors'
const CHIP_ON = 'border-sand-300/60 bg-sand-300/15 text-sand-100'
const CHIP_OFF = 'border-sand-500/20 text-ocean-200 hover:border-sand-300/40'

/**
 * Teintes de catégorie des pastilles de module ACTIVES : ocre pour le combat,
 * cyan pour l'utilitaire. Triplets repris à l'identique des pastilles de
 * `ShipPickerRow`, pour que « Canons longs » dans le tiroir et « Canons » sur une
 * ligne parlent la même langue visuelle.
 *
 * L'état inactif garde le `CHIP_OFF` neutre commun : teinter aussi les pastilles
 * éteintes ferait perdre de vue lesquelles sont sélectionnées.
 */
const CHIP_ON_COMBAT = 'text-orange-300 bg-orange-500/15 border-orange-500/30'
const CHIP_ON_UTILITY = 'text-ocean-300 bg-ocean-500/15 border-ocean-500/30'
const TOOL = 'min-h-8 inline-flex items-center gap-1 px-2 rounded border border-sand-500/20 text-[11px] text-ocean-200 hover:border-sand-300/40 transition-colors disabled:opacity-40'

export function ShipPickerFilterBar({
  ships, filter, selectedCount, cap, onTakeAll, onClearAll,
}: {
  /** TOUTES les coques à quai : les décomptes portent sur cet ensemble. */
  ships: ShipDto[]
  filter: ShipFilter
  selectedCount: number
  cap: number
  onTakeAll: () => void
  onClearAll: () => void
}) {
  const [drawer, setDrawer] = useState(false)
  const namedActive = [...filter.modules].filter((m) => m !== ANY_MODULE).length
  const equipped = ships.filter((s) => (s.modules ?? []).length > 0).length

  return (
    <div className="space-y-1.5 mb-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par type de coque">
        <button
          type="button"
          aria-pressed={filter.hulls.size === 0}
          onClick={filter.resetHulls}
          className={`${CHIP_BASE} font-semibold ${filter.hulls.size === 0 ? CHIP_ON : CHIP_OFF}`}
        >
          Tout <span className="text-ocean-400 tabular-nums">{ships.length}</span>
        </button>
        {SHIP_TYPE_ORDER.map((type) => {
          const n = ships.filter((s) => s.shipType === type).length
          if (n === 0) return null
          const on = filter.hulls.has(type)
          return (
            <button
              key={type}
              type="button"
              aria-pressed={on}
              onClick={() => filter.toggleHull(type)}
              className={`${CHIP_BASE} ${on ? CHIP_ON : CHIP_OFF}`}
            >
              <ShipIcon type={type} className="w-3.5 h-3.5" />
              {HULL_SHORT[type] ?? type} <span className="text-ocean-400 tabular-nums">{n}</span>
            </button>
          )
        })}
      </div>

      <div className="flex items-center justify-between gap-1.5 flex-wrap">
        <div className="flex gap-1.5">
          <button
            type="button"
            aria-pressed={filter.modules.has(ANY_MODULE)}
            onClick={() => filter.toggleModule(ANY_MODULE)}
            className={`${CHIP_BASE} ${filter.modules.has(ANY_MODULE) ? CHIP_ON : CHIP_OFF}`}
          >
            <ModuleIcon className="w-3.5 h-3.5" />
            Équipés <span className="text-ocean-400 tabular-nums">{equipped}</span>
          </button>
          <button
            type="button"
            onClick={() => setDrawer((d) => !d)}
            aria-expanded={drawer}
            className={TOOL}
          >
            Modules{namedActive > 0 && <span className="text-sand-300 tabular-nums"> {namedActive}</span>}
          </button>
        </div>
        <div className="flex gap-1.5">
          {filter.sorts.length > 1 && (
            <button type="button" onClick={filter.cycleSort} className={TOOL} title="Changer le tri">
              {SORT_LABEL[filter.sort]}
            </button>
          )}
          {selectedCount > 0 && (
            <button type="button" onClick={onClearAll} className={TOOL}>Tout lâcher</button>
          )}
          <button
            type="button"
            onClick={onTakeAll}
            disabled={selectedCount >= cap}
            className={`${TOOL} border-sand-300/50 text-sand-300`}
          >
            Tout prendre
          </button>
        </div>
      </div>

      {drawer && (
        <div className="flex flex-wrap gap-1.5 p-2 rounded bg-ocean-900/60 border border-sand-500/15" role="group" aria-label="Filtrer par module">
          {MODULE_FILTERS.map((m) => {
            const n = ships.filter((s) => (s.modules ?? []).some((f) => f.type === m.type)).length
            if (n === 0) return null
            const on = filter.modules.has(m.type)
            return (
              <button
                key={m.type}
                type="button"
                aria-pressed={on}
                onClick={() => filter.toggleModule(m.type)}
                className={`${CHIP_BASE} ${on ? (m.combat ? CHIP_ON_COMBAT : CHIP_ON_UTILITY) : CHIP_OFF}`}
              >
                {m.label} <span className="text-ocean-400 tabular-nums">{n}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
